import { useEffect, useRef, useState } from 'react';

import { SocketConnection } from '@/connection/socket/socket-main-thread/socket-connection';
import socketManager from '@/connection/socket/socket-main-thread/socket-manager';
import { updateProjectSettings, addLeaderboardNavItem } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { EventSettings } from '@/types/projects.types';
import { NavMainItems } from '@/types/homepage.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import toastTrigger from '@ui/toast/toast-trigger';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import { PointsEditModal } from './points-edit-modal';
import LeaderboardActionSettings from './action-settings';
import LeaderboardCSVUpload from './csv-upload';
import Modal from '@ui/modal/modal';
import { checkRegistrationToggled } from '@/utils/registration/check-registration-toggled';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import { getIsLoadingWorkingProject, getWorkingProject } from '@/store/slices/project/selectors';
import {
  getLeaderboardEnabled,
  getLeaderboardLoadingActions,
  getLeaderboardMainActions,
} from '@/store/slices/leaderboard/selectors';
import { loadActionPoints } from '@/store/slices/leaderboard';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import { EToastContainerIds } from '@ui/toast/toast.types';

import './action-settings.css';
import './points-edit-modal.css';
import BrandedLoadingIndicator from '@/components/branding/branded-loading-indicator';
import Text from '@ui/text/text/text';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import {
  TGlobalContainerDesigns,
  TGlobalIcoTextDesigns,
} from '@styles/design-system/global-components/global-components.types';
import SwitchV2 from '@ui/switch-v2/switch-v2';

const Leaderboard = () => {
  const user = useAppSelector(getUser);
  const active_channel = useAppSelector(getActiveChannel);
  const workingEvent = useAppSelector(getWorkingProject);
  const loadingEvent = useAppSelector(getIsLoadingWorkingProject);
  const leaderboardEnabled = !!useAppSelector(getLeaderboardEnabled);
  const loadingActions = useAppSelector(getLeaderboardLoadingActions);
  const currentActions = useAppSelector(getLeaderboardMainActions);
  const [toggleEditPointModal, setToggleEditPointModal] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const socket = useRef<SocketConnection | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const _event = workingEvent?.event ?? workingEvent?.event;
    if (_event && user) {
      dispatch(loadActionPoints({ channel: user.active_channel, eventId: _event }));
    }
  }, [dispatch, user, workingEvent]);

  useEffect(() => {
    const uploadComplete = () => {
      toastTrigger(
        <div>
          <Text tag="h6">Upload complete</Text>
          <Text tag="p">Your leaderboard custom actions upload has finished processing.</Text>
        </div>,
        { type: 'success' }
      );
    };

    const invalidCSV = () => {
      toastTrigger(
        <div>
          <Text tag="h6">Invalid File Uploaded</Text>
          <Text tag="p">
            The file was uploaded in an invalid format. Please upload a file that is formatted correctly.
          </Text>
        </div>,
        { type: 'error' }
      );
    };

    const uploadFailure = () => {
      toastTrigger(
        <div>
          <Text tag="h6">Error</Text>
          <Text tag="p">There was an error processing your upload.</Text>
        </div>,
        { type: 'error' }
      );
    };

    if (active_channel) {
      socket.current = socketManager.get(`admin-${active_channel}`);
      socket.current.addListener('leaderboard.complete', uploadComplete);
      socket.current.addListener('leaderboard.error.invalid', invalidCSV);
      socket.current.addListener('leaderboard.error.internal', uploadFailure);
    }

    return () => {
      socket.current?.removeListener('leaderboard.complete', uploadComplete);
      socket.current?.removeListener('leaderboard.error.invalid', invalidCSV);
      socket.current?.removeListener('leaderboard.error.internal', uploadFailure);
    };
  }, [active_channel, socket]);

  const handleToggleLeaderboard = (_: unknown, on: boolean) => {
    if (!on) {
      setConfirmationModalOpen(true);
    } else {
      toggleLeaderboard(on);
    }
  };

  const toggleLeaderboard = (on: boolean) => {
    if (workingEvent) {
      // registration required for leaderboard feature
      if (on) {
        if (!workingEvent.registration_on) {
          checkRegistrationToggled(true, workingEvent, dispatch);
          toastTrigger(
            <div>
              <Text tag="h6">Registration Turned On</Text>
              <Text tag="p">
                Enabling the Leaderboard feature requires attendees to register, including first and last name.
              </Text>
            </div>,
            { type: 'info', containerId: EToastContainerIds.stacked }
          );
        }

        toastTrigger(
          <div>
            <Text tag="h6">Leaderboard feature enabled</Text>
            <Text tag="p">
              Points will not apply retroactively for previously completed actions. A CSV upload can be used to update
              attendee scores manually.
            </Text>
          </div>,
          { type: 'info', containerId: EToastContainerIds.stacked }
        );
      }
      const eventSettings: EventSettings = {
        ...workingEvent.settings,
        leaderboard_settings: {
          ...workingEvent.settings.leaderboard_settings,
          enabled: on,
        },
      };
      dispatch(updateProjectSettings({ event_uuid: workingEvent.uuid, settings: eventSettings }));

      // create a leaderboard main nav item if it doesn't already exist and leaderboard tracking is turned on
      if (workingEvent.homepage?.event_main_nav_items) {
        const hasLeaderboardNavItem = workingEvent.homepage.event_main_nav_items.find(
          (ni) => ni.name === NavMainItems.Leaderboard
        );
        if (!hasLeaderboardNavItem && on) {
          const homepageId = workingEvent.homepage.homepage;
          dispatch(addLeaderboardNavItem({ eventUuid: workingEvent.uuid, homepageId }));
        }
      }
    }
  };

  const next = () => {
    setConfirmationModalOpen(false);
    toggleLeaderboard(false);
  };

  const handleEditActions = () => {
    // to prevent component re-renders/flashing we take loadingActions out of the wrapper conditional and move it here
    // however, since we are still loading the actions data, we do not want to allow the edit modal to be opened since we don't yet
    // have the data
    if (loadingActions) return;

    setToggleEditPointModal(true);
  };

  return (
    <>
      <Container design={TGlobalContainerDesigns.GlobalCont1} borderRadius="12px">
        <Container
          borderRadius="12px"
          padding="16px"
          flex="1"
          width="100%"
          design={TGlobalContainerDesigns.GlobalCont1}
          className="flex jc-space-between ai-center"
          gap="8px"
        >
          <Container
            design={TGlobalContainerDesigns.GlobalCont2}
            className="flex ai-center jc-center"
            borderRadius="8px"
            height="32px"
            width="32px"
            minHeight="32px"
            minWidth="32px"
          >
            <Icon name={ICONS.TROPHY_FILLED} size={16} />
          </Container>
          <Container className="flex flex-column gap-4" width="100%">
            <Text design={TGlobalIcoTextDesigns.GlobalIcoText1} className="H6" tag="h6">
              Leaderboard
            </Text>
          </Container>
          <Container>
            <SwitchV2
              on={leaderboardEnabled}
              onClick={handleToggleLeaderboard}
              value={'leaderboard'}
              disabled={false}
              loading={false}
              onLabel={'On'}
              offLabel={'Off'}
            />
          </Container>
        </Container>
        <OptionalComponent display={leaderboardEnabled}>
          <Container padding="0 16px 16px" className="flex flex-column gap-16">
            {!loadingEvent ? (
              <LeaderboardActionSettings
                actions={currentActions}
                editActions={handleEditActions}
                loadingActions={loadingActions}
              />
            ) : (
              <div className="loading-action-container">
                <BrandedLoadingIndicator />
              </div>
            )}

            {!loadingEvent && <LeaderboardCSVUpload />}
          </Container>
        </OptionalComponent>
      </Container>

      {!loadingEvent && (
        <PointsEditModal
          open={toggleEditPointModal}
          closeFn={setToggleEditPointModal}
          initialActions={currentActions}
        />
      )}
      <Modal
        open={confirmationModalOpen}
        onRequestClose={() => setConfirmationModalOpen(false)}
        renderHeaderContent={() => (
          <Container padding="12px 40px">
            <Text>Disabling Leaderboard</Text>
          </Container>
        )}
        renderFooterContent={() => (
          <Container className="flex fd-row jc-flex-end ai-center gap-8" padding="12px 40px">
            <Button
              onClick={() => setConfirmationModalOpen(false)}
              focusBackgroundColor="var(--gray-700)"
              className="L1"
            >
              Cancel
            </Button>
            <Button
              width="fit-content"
              height="32px"
              onClick={next}
              className="flex ai-center jc-center"
              backgroundColor={getThemeColorCSSVariable()}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Continue
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container padding="8px 40px">
          <Text>Are you sure you want to disable the Leaderboard feature?</Text>
        </Container>
      </Modal>
    </>
  );
};

export default Leaderboard;
